import { useCallback } from "react";
import { Project, Portfolio, Program } from "../api/query/useProjectQuery";
import { useUser } from "./useUser";
import dateFormatter from "../helperFuntions/dateFormater";
import { OrgListOfNonWorkingDaysEnum } from "@backend/src/schemas/organisationSchema";

export interface PortfolioAssignedUser {
  portfolioRole: string;
  user: {
    userId: string;
    email: string;
    firstName?: string;
    lastName?: string;
    avatarImg?: string;
  };
}

export interface ProgramAssignedUser {
  programRole: string;
  user: {
    userId: string;
    email: string;
    firstName?: string;
    lastName?: string;
    avatarImg?: string;
  };
}

export interface ExtendedProject extends Project {
  portfolio: (Portfolio & { assignedUsers?: PortfolioAssignedUser[] }) | null;
  program: (Program & { assignedUsers?: ProgramAssignedUser[] }) | null;
}

export interface ProjectNode extends ExtendedProject {
  type: "project";
  progress: number;
}

export interface ProgramNode {
  type: "program";
  programId: string;
  programName: string;
  progress: number;
  actualProgress: number;
  expectedProgress: number;
  portfolioId: string | undefined;
  projects: ProjectNode[];
  assignedUsers?: ProgramAssignedUser[];
  __totalDuration?: number;
  __weightedProgress?: number;
  __weightedExpected?: number;
}

export interface PortfolioNode {
  type: "portfolio";
  portfolioId: string;
  portfolioName: string;
  progress: number;
  actualProgress: number;
  expectedProgress: number;
  projects: ProjectNode[];
  programs: ProgramNode[];
  assignedUsers?: PortfolioAssignedUser[];
  __totalDuration?: number;
  __weightedProgress?: number;
  __weightedExpected?: number;
}

export type HierarchyItem = PortfolioNode | ProgramNode | ProjectNode;

type Calculable<T> = T & {
  __totalDuration: number;
  __weightedProgress: number;
  __weightedExpected: number;
};

const UTCDateConverter = (date: Date) => {
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0),
  );
};

export function useProjectHierarchy() {
  const { user } = useUser();

  const nonWorkingDays = useCallback(() => {
    const nWd =
      user?.userOrganisation?.map((org) => org.organisation.nonWorkingDays) ||
      [];
    const mapDayToNumber = (day: OrgListOfNonWorkingDaysEnum) => {
      switch (day) {
        case "SUN":
          return 0;
        case "MON":
          return 1;
        case "TUE":
          return 2;
        case "WED":
          return 3;
        case "THU":
          return 4;
        case "FRI":
          return 5;
        case "SAT":
          return 6;
        default:
          return 0;
      }
    };
    const result: number[] = [];
    nWd.forEach((days) => {
      days.forEach((day) => {
        result.push(mapDayToNumber(day));
      });
    });
    return result;
  }, [user]);

  // Using your existing duration calculation for business logic consistency
  const calculateDurations = useCallback(
    (startDate: Date, endDate: Date): number => {
      const start = UTCDateConverter(new Date(startDate));
      const end = UTCDateConverter(new Date(endDate));

      end.setDate(end.getDate() + 1);
      const excludedDays = nonWorkingDays();
      const holidays =
        user?.userOrganisation?.[0]?.organisation?.orgHolidays?.map((h) =>
          dateFormatter(new Date(h.holidayStartDate)),
        ) ?? [];

      let duration = 0;
      for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
        const day = d.getDay();
        const formattedDate = d.toISOString().split("T")[0];
        if (
          !excludedDays.includes(day) &&
          !holidays.includes(dateFormatter(new Date(formattedDate)))
        ) {
          duration++;
        }
      }
      return duration;
    },
    [user, nonWorkingDays],
  );

  const calculateProjectMetrics = useCallback(
    (project: ExtendedProject) => {
      const { actualStartDate, actualEndDate, progressionPercentage } = project;
      const expectedRaw = Number(project.expectedprogress || 0);

      if (!actualStartDate || !actualEndDate) {
        return { duration: 0, weightedProgress: 0, progress: 0, expectedProgress: expectedRaw, weightedExpected: 0 };
      }

      const duration = calculateDurations(
        new Date(actualStartDate),
        new Date(actualEndDate),
      );
      const progress = Math.round(Number(progressionPercentage || 0) * 100);
      const weightedProgress = progress * duration;
      const weightedExpected = expectedRaw * duration;

      return { duration, weightedProgress, progress, expectedProgress: expectedRaw, weightedExpected };
    },
    [calculateDurations],
  );

  const buildCompleteHierarchy = useCallback(
    (projectsList: Project[]): HierarchyItem[] => {
      const extendedList = projectsList as ExtendedProject[];
      const portfolioMap = new Map<string, Calculable<PortfolioNode>>();
      const orphanProgramMap = new Map<string, Calculable<ProgramNode>>();
      const standaloneProjects: ProjectNode[] = [];

      for (const project of extendedList) {
        if (!project.projectId) continue;

        const { duration, weightedProgress, progress, weightedExpected } =
          calculateProjectMetrics(project);
        const projectNode: ProjectNode = {
          ...project,
          type: "project",
          progress,
        };

        const hasPortfolio = !!project.portfolio?.portfolioId;
        const hasProgram = !!project.program?.programId;

        if (hasPortfolio) {
          const portfolioId = project.portfolio!.portfolioId;
          if (!portfolioMap.has(portfolioId)) {
            portfolioMap.set(portfolioId, {
              type: "portfolio",
              portfolioId,
              portfolioName:
                project.portfolio!.portfolioName || "Unnamed Portfolio",
              progress: 0,
              actualProgress: 0,
              expectedProgress: 0,
              projects: [],
              programs: [],
              assignedUsers: project.portfolio!.assignedUsers,
              __totalDuration: 0,
              __weightedProgress: 0,
              __weightedExpected: 0,
            });
          }
          const portfolioEntry = portfolioMap.get(portfolioId)!;
          portfolioEntry.__totalDuration += duration;
          portfolioEntry.__weightedProgress += weightedProgress;
          portfolioEntry.__weightedExpected += weightedExpected;

          if (hasProgram) {
            const programId = project.program!.programId;
            let programEntry = portfolioEntry.programs.find(
              (p) => p.programId === programId,
            ) as Calculable<ProgramNode> | undefined;

            if (!programEntry) {
              programEntry = {
                type: "program",
                programId,
                programName: project.program!.programName || "Unnamed Program",
                progress: 0,
                actualProgress: 0,
                expectedProgress: 0,
                portfolioId: portfolioEntry.portfolioId,
                projects: [],
                assignedUsers: project.program!.assignedUsers,
                __totalDuration: 0,
                __weightedProgress: 0,
                __weightedExpected: 0,
              };
              portfolioEntry.programs.push(programEntry as ProgramNode);
            }

            programEntry.__totalDuration += duration;
            programEntry.__weightedProgress += weightedProgress;
            programEntry.__weightedExpected += weightedExpected;
            programEntry.projects.push(projectNode);
          } else {
            portfolioEntry.projects.push(projectNode);
          }
        } else if (hasProgram) {
          const programId = project.program!.programId;
          if (!orphanProgramMap.has(programId)) {
            orphanProgramMap.set(programId, {
              type: "program",
              programId,
              programName: project.program!.programName || "Unnamed Program",
              progress: 0,
              actualProgress: 0,
              expectedProgress: 0,
              portfolioId: undefined,
              projects: [],
              assignedUsers: project.program!.assignedUsers,
              __totalDuration: 0,
              __weightedProgress: 0,
              __weightedExpected: 0,
            });
          }
          const programEntry = orphanProgramMap.get(programId)!;
          programEntry.__totalDuration += duration;
          programEntry.__weightedProgress += weightedProgress;
          programEntry.__weightedExpected += weightedExpected;
          programEntry.projects.push(projectNode);
        } else {
          standaloneProjects.push(projectNode);
        }
      }

      const finalPortfolios = Array.from(portfolioMap.values())?.map((p) => {
        p.programs.forEach((prog) => {
          const program = prog as Calculable<ProgramNode>;
          program.progress =
            program.__totalDuration > 0
              ? Math.round(program.__weightedProgress / program.__totalDuration)
              : 0;
          program.actualProgress = program.progress;
          program.expectedProgress =
            program.__totalDuration > 0
              ? Math.round(program.__weightedExpected / program.__totalDuration)
              : 0;
          const baseProg = program as ProgramNode;
          delete baseProg.__totalDuration;
          delete baseProg.__weightedProgress;
          delete baseProg.__weightedExpected;
        });
        p.progress =
          p.__totalDuration > 0
            ? Math.round(p.__weightedProgress / p.__totalDuration)
            : 0;
        p.actualProgress = p.progress;
        p.expectedProgress =
          p.__totalDuration > 0
            ? Math.round(p.__weightedExpected / p.__totalDuration)
            : 0;
        const baseP = p as PortfolioNode;
        delete baseP.__totalDuration;
        delete baseP.__weightedProgress;
        delete baseP.__weightedExpected;
        return p;
      });

      const finalOrphanPrograms = Array.from(orphanProgramMap.values())?.map(
        (p) => {
          p.progress =
            p.__totalDuration > 0
              ? Math.round(p.__weightedProgress / p.__totalDuration)
              : 0;
          p.actualProgress = p.progress;
          p.expectedProgress =
            p.__totalDuration > 0
              ? Math.round(p.__weightedExpected / p.__totalDuration)
              : 0;
          const baseP = p as ProgramNode;
          delete baseP.__totalDuration;
          delete baseP.__weightedProgress;
          delete baseP.__weightedExpected;
          return p;
        },
      );

      return [
        ...finalPortfolios,
        ...finalOrphanPrograms,
        ...standaloneProjects,
      ];
    },
    [calculateProjectMetrics],
  );

  return {
    buildCompleteHierarchy,
  };
}
